<template>
  <div ref="map-container" class="md-layout">
    <l-map
      ref="map"
      id="map"
      :zoom="mapData.zoom"
      :center="mapData.center"
      @update:zoom="zoomUpdated"
      @update:center="filterMarkers"
    >
      <l-control position="topright">
        <div :class="categoryDropdownVisible ? 'dropdown open' : 'dropdown' ">
          <button type="button" class="categories-dropdown" @click="categoryButtonClick">
            {{$t('Categories')}}
            <i class="md-icon md-icon-font">keyboard_arrow_down</i>
          </button>

          <ul class="dropdown-menu dropdown-menu-right">
            <li v-for="item in categories" :key="item.key" style="cursor: pointer;">
              <a
                @click="categoryClick(item.id)"
                :class="[
                      { active: isSelected(item.id) },
                    ]"
              >
                <div class="dropdown-image-container">
                  <img :src="getMarkerUrl(item.id)" />
                </div>
                <div class="md-layout-item">{{ language.code == 0 ? item.title_PL : item.title_EN }}</div>
                <div style="text-align: right; width: 24px;">
                  <i
                    class="md-icon md-icon-font"
                    :class="[
                          { hidden: !isSelected(item.id) },
                        ]"
                  >check</i>
                </div>
              </a>
            </li>
          </ul>
        </div>
      </l-control>
      <l-control-scale position="bottomleft" :imperial="false" :metric="true"></l-control-scale>
      <l-tile-layer :url="mapData.tilesUrl"></l-tile-layer>
      <l-marker
        v-for="marker in filteredMarkers"
        :key="marker.id"
        :visible="true"
        :lat-lng="marker.latLng"
        :icon="getMarkerIcon(marker.markerFileId)"
      >
        <l-popup>
          <div class="popup-content">
            <strong>{{ language.code == 0 ? marker.name_PL : marker.name_EN }}</strong>
            <br />
            <br />
            {{ language.code == 0 ? marker.description_PL : marker.description_EN }}
            <br v-if="marker.information.length > 0 || marker.buildingId" />
            <br v-if="marker.information.length > 0 || marker.buildingId" />
            <a
              v-if="marker.information.length > 0"
              @click="showDetails(marker)"
            >{{$t('Show details')}}</a>
            <br />
            <a
              v-if="marker.buildingId"
              @click="selectedBuildingId = marker.buildingId"
            >{{$t('Building plans')}}</a>
          </div>
        </l-popup>
      </l-marker>
    </l-map>
    <div class="modal modal-bg" :class="{ show: selectedItem }">
      <modal v-if="selectedItem" @close="selectedItem = null">
        <template slot="header">
          <md-button
            class="md-simple md-close md-just-icon md-round modal-default-button"
            @click="selectedItem = null"
          >
            <md-icon>clear</md-icon>
          </md-button>
        </template>

        <template slot="body">
          <div v-html="selectedItem.information[language.code].content" />
        </template>
      </modal>
    </div>
    <div class="modal modal-bg" :class="{ show: selectedBuildingId }">
      <modal v-if="selectedBuildingId" @close="selectedBuildingId = null" id="building-modal">
        <template slot="header">
          <md-button
            class="md-simple md-close md-just-icon md-round modal-default-button"
            @click="selectedBuildingId = null"
          >
            <md-icon>clear</md-icon>
          </md-button>
        </template>

        <template slot="body">
          <building-details :id="selectedBuildingId" />
        </template>
      </modal>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import axios from "@/util/axios";

import "leaflet/dist/leaflet.css";
import Vue from "vue";
import L from "leaflet";
import {
  LMap,
  LTileLayer,
  LMarker,
  LPopup,
  LPolyline,
  LControlScale,
  LFeatureGroup,
  LControl,
  LIcon
} from "vue2-leaflet";
import { Icon } from "leaflet";
import { Modal } from "@/components";
import BuildingDetails from "@/pages/authenticated/BuildingDetailsPage.vue";

import "./Map.Leaflet.Editable.js";

delete Icon.Default.prototype._getIconUrl;

Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png")
});

Vue.component("l-map", LMap);
Vue.component("l-tile-layer", LTileLayer);
Vue.component("l-marker", LMarker);
Vue.component("l-popup", LPopup);
Vue.component("l-control-scale", LControlScale);
Vue.component("l-control", LControl);
Vue.component("l-icon", LIcon);

export default {
  components: { Modal, BuildingDetails },
  data() {
    return {
      mapData: {
        tilesUrl: "https://{s}.tile.osm.org/{z}/{x}/{y}.png",
        zoom: 6,
        center: {
          lat: 52.112795,
          lng: 19.211946
        }
      },
      marker: null,
      markers: [],
      filteredMarkers: [],
      polylines: [],
      categories: [],
      selectedCategories: [],
      selectedItem: null,
      selectedBuildingId: null,
      categoryDropdownVisible: false,
      currentZoom: 0
    };
  },
  computed: {
    ...mapGetters({
      conference: "conference/current",
      language: "language/current"
    })
  },
  methods: {
    categoryButtonClick() {
      this.categoryDropdownVisible = !this.categoryDropdownVisible;
    },
    filterMarkers() {
      let self = this;

      if (this.selectedCategories.length == 0) {
        this.filteredMarkers = this.markers;
        this.zoomUpdated(this.currentZoom);
        return;
      }

      this.filteredMarkers = this.markers.filter(function(item) {
        return self.selectedCategories.some(function(item2) {
          var itemCategory = self.categories.find(
            c => c.id === item.categoryId
          );
          if (itemCategory) {
            var isInMin = true;
            var isInMax = true;

            if (itemCategory.minZoom) {
              isInMin = itemCategory.minZoom <= self.currentZoom;
            }
            if (itemCategory.maxZoom) {
              isInMax = itemCategory.maxZoom >= self.currentZoom;
            }
            return isInMin && isInMax && item2 == item.categoryId;
          }

          return item2 == item.categoryId;
        });
      });
    },
    zoomUpdated(zoom) {
      this.currentZoom = zoom;
      var categories = [];
      if (this.selectedCategories.length > 0) {
        categories = this.categories.filter(c =>
          this.selectedCategories.includes(c.id)
        );
      } else {
        categories = this.categories;
      }

      this.filteredMarkers = this.markers.filter(function(item) {
        if (item) {
          var itemCategory = categories.find(c => c.id === item.categoryId);
          if (itemCategory) {
            var isInMin = true;
            var isInMax = true;

            if (itemCategory.minZoom) {
              isInMin = itemCategory.minZoom <= zoom;
            }
            if (itemCategory.maxZoom) {
              isInMax = itemCategory.maxZoom >= zoom;
            }
            return isInMin && isInMax;
          }
        }
        return true;
      });
    },
    getMarkerUrl(categoryId) {
      var temp = this.markers.find(function(item) {
        return item.categoryId == categoryId;
      });

      if (temp != undefined && temp.markerFileId != null) {
        var markerFileId = temp.markerFileId;
        return axios.getFileUrl(markerFileId);
      }

      return require("@/assets/img/marker-icon-2x.png");
    },
    getMarkerIcon(id) {
      if (id != null) {
        return L.icon({
          iconUrl: axios.getFileUrl(id),
          iconSize: [26, 41],
          iconAnchor: [13, 41],
          popupAnchor: [0, -33]
        });
      }
    },
    isSelected(id) {
      return this.selectedCategories.some(function(item) {
        return item == id;
      });
    },
    categoryClick(categoryId) {
      let self = this;

      if (this.isSelected(categoryId)) {
        this.selectedCategories = this.selectedCategories.filter(function(
          item
        ) {
          return item != categoryId;
        });
      } else {
        this.selectedCategories.push(categoryId);
      }

      this.filterMarkers();
    },
    showDetails(marker) {
      this.selectedItem = marker;
    },
    async loadData() {
      let self = this;
      let loader = this.$loading.show({ container: this.$refs.page });

      try {
        let data = (
          await axios.get("public/maps", {
            params: {
              conferenceYear: this.conference.year,
              conferenceType: this.conference.type
            }
          })
        ).data;

        this.mapData.tilesUrl = data.tilesUrl;

        let categories = (
          await axios.get("public/maps/categories", {
            params: {
              conferenceYear: this.conference.year,
              conferenceType: this.conference.type
            }
          })
        ).data;

        if (categories != null) {
          this.categories = categories.items;
        }

        let markers = (
          await axios.get("public/maps/markers", {
            params: {
              conferenceYear: this.conference.year,
              conferenceType: this.conference.type
            }
          })
        ).data;

        markers = markers.map(function(item) {
          item.latLng = { lat: item.lat, lng: item.long };
          self.markers.push(item);
          return item;
        });

        this.filteredMarkers = this.markers;

        let polylines = (
          await axios.get("public/maps/polylines", {
            params: {
              conferenceYear: this.conference.year,
              conferenceType: this.conference.type
            }
          })
        ).data;

        for (var i in polylines) {
          let pl = polylines[i];

          var polyline = L.polyline(
            pl.points.map(function(item) {
              return [item.lat, item.long];
            })
          );

          polyline.id = pl.id;
          polyline.lastModifiedDate = pl.lastModifiedDate;

          if (pl.color != null) {
            polyline.setStyle({ color: pl.color });
          }

          polyline.addTo(this.$refs.map.mapObject);

          this.polylines.push(polyline);
        }

        this.$refs.map.setZoom(data.zoom);
        this.$refs.map.setCenter([data.lat, data.long]);
      } catch (exception) {
      } finally {
        loader.hide();
      }
    }
  },
  async mounted() {
    await this.loadData();
  }
};
</script>
<style lang="scss">
#map {
  height: 600px;
}

.categories-dropdown {
  cursor: pointer;
  line-height: 26px;
  background-color: #fff;
  border: 0px;
  border-radius: 4px;
  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.65);
  margin-bottom: 2px;
}

.hidden {
  display: none;
}

.popup-content a {
  cursor: pointer;
  color: #000 !important;
}

.popup-content a:hover {
  color: #3f51b5 !important;
}

.leaflet-container .leaflet-marker-pane img {
  max-width: 50px !important;
  max-height: 82px !important;
}

.dropdown-menu img {
  max-width: 32px;
  max-height: 32px;
}

.dropdown-image-container {
  width: 32px;
  text-align: center;
  margin-right: 4px;
}
#building-modal .modal-body {
  padding-top: 0px !important;
  padding-right: 0px !important;
  padding-left: 0px !important;
}
</style>
